// Field tasks from a selection (guide 19.4, "Tasks"): the picked rows become a
// task with its element list, quantities and its own sheet page, and a title
// that is generated, not typed ("Install 29 telecom sleeves, Level 2, grids
// 12 to 10, 406 ft"). Closing a task asks for each element's status; those
// are written as field status events, so the task never carries a
// percentage of its own. Backend: routes/technicalViews.js /:viewId/tasks.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Input, Modal, Select, Table, Tag, message } from "antd";
import { viewsApi } from "./viewModel";

const STATUS_LABEL = { projected: "Projected", procured: "Procured", being_installed: "Being installed", installed: "Installed" };
const STATUS_OPTIONS = Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }));

export default function FieldTasks({ projectId, view, picked, elements, canEdit, canAdd = false, onChanged, onPicked }) {
  const [tasks, setTasks] = useState(null);
  const [making, setMaking] = useState(null);     // { title, name, email, due } while the dialog is open
  const [closing, setClosing] = useState(null);   // { task, statuses: { rowId: status } }
  const [busy, setBusy] = useState(false);
  const [suggested, setSuggested] = useState(null);  // { list, picked: Set<index> } (guide 19.9: by level and zone)

  const load = useCallback(() => {
    viewsApi(projectId, `/${view.viewId}/tasks`).then((o) => setTasks(o.tasks || [])).catch(() => setTasks([]));
  }, [projectId, view.viewId]);
  useEffect(() => { load(); }, [load]);

  const statusOf = useMemo(() => {
    const m = new Map();
    (elements || []).forEach((e) => m.set(e.id, e.status || "projected"));
    return m;
  }, [elements]);

  const make = async () => {
    setBusy(true);
    try {
      const t = await viewsApi(projectId, `/${view.viewId}/tasks`, {
        method: "POST",
        body: { rowIds: picked, title: making.title || undefined, assignee: { name: making.name, email: making.email }, dueDate: making.due || undefined },
      });
      message.success(`${t.taskId} made: ${t.title}. Its sheet page is with the other pages below.`);
      setMaking(null);
      onPicked([]);
      load();
      onChanged();
    } catch (e) { message.error(e.message); } finally { setBusy(false); }
  };

  // Closing a task most often means the work is done: every element starts at
  // installed, and the assignee changes the ones that are not.
  const openClose = (task) => setClosing({ task, statuses: Object.fromEntries(task.rowIds.map((id) => [id, "installed"])) });
  const close = async () => {
    setBusy(true);
    try {
      const out = await viewsApi(projectId, `/${view.viewId}/tasks/${closing.task.taskId}/close`, {
        method: "POST", body: { confirmations: Object.entries(closing.statuses).map(([rowId, status]) => ({ rowId, status })) },
      });
      const skipped = (out.refusedLinked || []).length;
      message.success(`${closing.task.taskId} closed: ${out.written} statuses written${skipped ? `; ${skipped} linked to the model are set on the tracking board` : ""}`);
      setClosing(null);
      load();
      onChanged();
    } catch (e) { message.error(e.message); } finally { setBusy(false); }
  };
  const cancel = (task) => Modal.confirm({
    title: `Cancel ${task.taskId}?`, content: "The task is dropped; no status changes.", okText: "Cancel the task", cancelText: "Keep it",
    onOk: async () => { await viewsApi(projectId, `/${view.viewId}/tasks/${task.taskId}/cancel`, { method: "POST", body: {} }); load(); },
  });

  const suggest = async (from = "") => {
    try {
      const out = await viewsApi(projectId, `/${view.viewId}/tasks/suggest${from ? `?from=${from}` : ""}`);
      const list = out.suggestions || [];
      if (!list.length) {
        return message.info(from === "lookahead"
          ? "No linked activity starts in the next two weeks with elements not started (link rows to activities first)"
          : "Everything on this view is installed or already in an open task");
      }
      setSuggested({ list, from, picked: new Set(list.map((_, i) => i)) });
    } catch (e) { message.error(e.message); }
    return undefined;
  };
  const makeSuggested = async () => {
    setBusy(true);
    let made = 0;
    try {
      for (const [i, g] of suggested.list.entries()) {
        if (!suggested.picked.has(i)) continue;
        // eslint-disable-next-line no-await-in-loop
        await viewsApi(projectId, `/${view.viewId}/tasks`, { method: "POST", body: { rowIds: g.rowIds, ...(g.activityId ? { ganttItemId: g.activityId, title: g.title } : {}) } });
        made += 1;
      }
      message.success(`${made} task${made === 1 ? "" : "s"} made, each with its own sheet page`);
      setSuggested(null); load(); onChanged();
    } catch (e) { message.error(`${made} made, then: ${e.message}`); load(); } finally { setBusy(false); }
  };

  const open = (tasks || []).filter((t) => t.status === "open");
  const installed = (t) => t.rowIds.filter((id) => statusOf.get(id) === "installed").length;

  return (
    <div className="g-tasks">
      {canAdd && (
        <div className="g-actions g-noprint" style={{ margin: "4px 0 8px" }}>
          <Button size="small" disabled={!picked.length} onClick={() => setMaking({ title: "", name: "", email: "", due: "" })}>
            Make a task{picked.length ? ` (${picked.length})` : ""}
          </Button>
          {!picked.length && <span className="g-tsub">Pick rows to make a task from them, or</span>}
          <Button size="small" onClick={() => suggest()}>Suggest tasks by level and zone</Button>
          <Button size="small" onClick={() => suggest("lookahead")}>From the look-ahead</Button>
        </div>
      )}
      {tasks && tasks.length > 0 && (
        <div style={{ margin: "8px 0 16px" }}>
          <b>Tasks</b>
          <span className="g-tsub"> · {open.length} open</span>
          <div className="g-tablewrap">
          <Table size="small" rowKey="taskId" pagination={tasks.length > 10 ? { pageSize: 10 } : false} dataSource={tasks}
                 columns={[
                   { title: "Task", dataIndex: "taskId", width: 70 },
                   { title: "What", dataIndex: "title", render: (t, r) => (
                     <span>{t}{r.assignee && r.assignee.name ? <div className="g-tsub">for {r.assignee.name}{r.dueDate ? `, due ${String(r.dueDate).slice(0, 10)}` : ""}</div> : r.dueDate ? <div className="g-tsub">due {String(r.dueDate).slice(0, 10)}</div> : null}</span>
                   ) },
                   { title: "On site", width: 130, render: (_, r) => `${installed(r)} of ${r.rowIds.length} installed` },
                   { title: "", width: 190, render: (_, r) => (r.status === "open" ? (
                     <span className="g-actions">
                       <Button size="small" onClick={() => onPicked(r.rowIds)}>Select</Button>
                       {canEdit && <Button size="small" type="primary" onClick={() => openClose(r)}>Close</Button>}
                       {canEdit && <Button size="small" type="text" onClick={() => cancel(r)}>Cancel</Button>}
                     </span>
                   ) : <Tag>{r.status === "closed" ? `Closed${r.closedByName ? ` by ${r.closedByName}` : ""}` : r.status}</Tag>) },
                 ]} />
          </div>
        </div>
      )}

      <Modal open={!!suggested} width={680} title={suggested && suggested.from === "lookahead" ? "Tasks from the look-ahead (the next two weeks)" : "Tasks by level and zone"} okText={suggested ? `Make ${suggested.picked.size} task${suggested.picked.size === 1 ? "" : "s"}` : "Make"}
             okButtonProps={{ disabled: !suggested || !suggested.picked.size }} confirmLoading={busy} onCancel={() => setSuggested(null)} onOk={makeSuggested}>
        {suggested && (
          <>
            <p className="g-tsub">What is not installed yet, level by level from the bottom up and zone by zone along the grid. Elements already in an open task are left out.</p>
            {suggested.list.map((g, i) => (
              <div key={i} style={{ margin: "4px 0" }}>
                <Checkbox checked={suggested.picked.has(i)} onChange={(e) => {
                  const next = new Set(suggested.picked); if (e.target.checked) next.add(i); else next.delete(i);
                  setSuggested({ ...suggested, picked: next });
                }}>{g.title}</Checkbox>
              </div>
            ))}
          </>
        )}
      </Modal>

      <Modal open={!!making} title="Make a task from the selection" okText="Make the task" confirmLoading={busy} onCancel={() => setMaking(null)} onOk={make}>
        {making && (
          <>
            <p className="g-tsub">The task carries these {picked.length} elements, their quantities and a sheet page of its own. Its title is made from them unless you type one.</p>
            <label htmlFor="task-title">Title
              <Input id="task-title" value={making.title} maxLength={200} placeholder="Made from the selection" onChange={(e) => setMaking({ ...making, title: e.target.value })} />
            </label>
            <label htmlFor="task-who">Who does it
              <Input id="task-who" value={making.name} maxLength={120} placeholder="Name" onChange={(e) => setMaking({ ...making, name: e.target.value })} />
            </label>
            <label htmlFor="task-email">Their email (optional)
              <Input id="task-email" value={making.email} maxLength={200} onChange={(e) => setMaking({ ...making, email: e.target.value })} />
            </label>
            <label htmlFor="task-due">Due (optional)
              <Input id="task-due" type="date" value={making.due} onChange={(e) => setMaking({ ...making, due: e.target.value })} />
            </label>
          </>
        )}
      </Modal>

      <Modal open={!!closing} width={640} title={closing ? `Close ${closing.task.taskId}` : ""} okText="Close the task" confirmLoading={busy} onCancel={() => setClosing(null)} onOk={close}>
        {closing && (
          <>
            <p className="g-tsub">{closing.task.title}. Confirm where each element is now; each answer is written to its history with your name.</p>
            <Button size="small" style={{ marginBottom: 8 }} onClick={() => setClosing({ ...closing, statuses: Object.fromEntries(closing.task.rowIds.map((id) => [id, "installed"])) })}>All installed</Button>
            <Table size="small" rowKey="id" pagination={closing.task.rowIds.length > 12 ? { pageSize: 12 } : false}
                   dataSource={closing.task.rowIds.map((id) => ({ id }))}
                   columns={[
                     { title: "Element", dataIndex: "id", width: 100 },
                     { title: "Was", width: 140, render: (_, r) => STATUS_LABEL[statusOf.get(r.id)] || "Projected" },
                     { title: "Now", render: (_, r) => (
                       <Select size="small" style={{ width: 170 }} value={closing.statuses[r.id]} options={STATUS_OPTIONS} aria-label={`Status of ${r.id}`}
                               onChange={(v) => setClosing({ ...closing, statuses: { ...closing.statuses, [r.id]: v } })} />
                     ) },
                   ]} />
          </>
        )}
      </Modal>
    </div>
  );
}
